import type { Rental } from '@aia/contracts';
import type { InitOptions } from 'i18next';

import { getCookieNames } from '@aia/utils/cookies';

import type { AVAILABLE_LOCALES } from './constants';

export type ValidCurrency = Exclude<keyof Rental['rates'], '__typename'>;
export type ValidLocale = (typeof AVAILABLE_LOCALES)[number];

type I18nOption = {
  name: string;
  symbol: string;
};

export interface LocaleOption extends I18nOption {
  code: ValidLocale;
}

export interface CurrencyOption extends I18nOption {
  code: ValidCurrency;
}

export const languageOptions: LocaleOption[] = [
  { code: 'en', name: 'English', symbol: 'EN' },
  { code: 'pt', name: 'Português', symbol: 'PT' },
  { code: 'fr', name: 'Français', symbol: 'FR' },
  { code: 'de', name: 'Deutsch', symbol: 'DE' },
];

export const currencyOptions: CurrencyOption[] = [
  { code: 'eur', symbol: '€', name: 'Euro' },
  { code: 'usd', symbol: '$', name: 'US Dollar' },
  { code: 'gbp', symbol: '£', name: 'British Pound' },
  { code: 'chf', symbol: 'CHF', name: 'Swiss Franc' },
];

// Convert an array of ValidLocale to BCP47 format
export const localesToBCP47 = (locales: Readonly<Array<ValidLocale>>) => {
  return [...locales].map(localeToBCP47);
};

// Convert a single ValidLocale to BCP47 format
export const localeToBCP47 = (locale: Readonly<ValidLocale>) => {
  const mapping: Record<ValidLocale, string> = {
    en: 'en-US',
    pt: 'pt-PT',
    fr: 'fr-FR',
    de: 'de-DE',
  };

  return mapping[locale];
};

// Convert a BCP47 language tag to a ValidLocale
export const fromBCP47ToLocale = (bcp47: string): ValidLocale => {
  return bcp47.split('-')[0] as ValidLocale; // Remove any quality values
};

// I18next options for the web application
const { localeCookieName } = getCookieNames('web');
// No `resources` here: the server bundles every locale (server/i18nextInstance.ts), the
// browsers load their language on demand (lazyResources.ts).
export const defaultI18nextOptions: InitOptions = {
  fallbackLng: 'en' as ValidLocale,
  defaultNS: 'content',
  detection: {
    cookieName: localeCookieName,
  },
  interpolation: {
    escapeValue: false, // not needed for react
    skipOnVariables: false, // needed to allow nesting
  },
};
